import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from '../api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_user')); }
    catch { return null; }
  });
  const [token, setToken]     = useState(() => localStorage.getItem('admin_token') || null);
  const [loading, setLoading] = useState(false);

  const isAuthenticated = Boolean(token && user);
  const isSuperAdmin    = user?.role === 'superadmin';

  // Refresh user data from server on mount (keeps role/email in sync after updates)
  useEffect(() => {
    if (!token) return;
    authAPI.me()
      .then(({ data }) => {
        if (data.status) {
          setUser(data.data);
          localStorage.setItem('admin_user', JSON.stringify(data.data));
        }
      })
      .catch(() => {}); // silently ignore — stale token handled by axios interceptor
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAuth = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    localStorage.setItem('admin_token', authToken);
  };

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_token');
  };

  const login = useCallback(async (credentials) => {
    setLoading(true);
    try {
      const { data } = await authAPI.login(credentials);
      if (data.status) {
        saveAuth(data.data.admin, data.data.token);
      }
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch (_) {}
    clearAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isSuperAdmin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
